/**
 * Member detail's URL view state (DNKADM-0430 P1-D): which tab is open, and each tab's
 * list state, so a copied link or a reload reopens the same view.
 *
 * Split from the components so they export components only
 * (`react-refresh/only-export-components`) and so the rules are testable alone.
 *
 * URL params on `/members/:contactId` (all optional; a default is never written). Each
 * tab's params carry its own prefix, so one tab never reads or clears another's:
 * - `tab`: `timeline` | `credits` | `payment-methods` | `payments` | `reservations` |
 *   `profile` | `activity` | `guests`. Default `credits`. Every operator, STAFF included,
 *   has all eight (DNKADM-0465); any other value opens Credits.
 * - Activity: `activity` = `credit-history` (default) | `ledger`, the inner tab.
 * - Activity > Credit history: `chFrom`, `chTo` (`YYYY-MM-DD`, default the last 30 days;
 *   a cleared date is `chFrom=`), `chReason`, `chBucket`, `chPage` (1-indexed).
 * - Payment methods: `pmSort` + `pmDir` (default Added, newest first).
 * - Payments: `payPage` (1-indexed).
 * - Guests: `guestStatus` (default `all`), `guestSort` + `guestDir` (default Last invited,
 *   newest first).
 * Reservations, Membership/Credits, Profile and Timeline keep no list state in the URL.
 */
import type { MemberCard } from '@/api/memberCards';
import type { MemberGuest, MemberGuestStatus, MemberGuestStatusFilter } from '@/api/memberGuests';
import { toTime, type SortState, type SortValue } from '@/components/list/listSort';
import { rangeBounds } from '@/components/list/listDate';
import type { SortControlOption } from '@/components/list/SortControl';
import { cardLabel } from './memberCardDisplay';

// --- Outer tab ----------------------------------------------------------------------

/**
 * Each tab's `tab=` URL value, keyed by its MUI `Tab` value. The numeric values are the
 * existing explicit ones (DNKADM-0361/0391/0429), which the a11y ids are built from.
 */
export const MEMBER_DETAIL_TAB_PARAM: Record<number, string> = {
  7: 'timeline',
  0: 'credits',
  1: 'payment-methods',
  6: 'payments',
  2: 'reservations',
  3: 'profile',
  4: 'activity',
  5: 'guests',
};

/** The URL params; default tab Membership/Credits (DNKADM-0235), for every operator. */
export const MEMBER_DETAIL_URL_DEFAULTS = { tab: 'credits' };

/** Read the `tab` param as a MUI `Tab` value. An unknown value opens Membership/Credits. */
export function parseMemberDetailTab(raw: string): number {
  const entry = Object.entries(MEMBER_DETAIL_TAB_PARAM).find(([, param]) => param === raw);
  return entry === undefined ? 0 : Number(entry[0]);
}

// --- Activity inner tab -------------------------------------------------------------

export const MEMBER_ACTIVITY_TABS = ['credit-history', 'ledger'] as const;
export type MemberActivityInnerTab = (typeof MEMBER_ACTIVITY_TABS)[number];
export const MEMBER_ACTIVITY_URL_DEFAULTS = { activity: 'credit-history' };

// --- Activity > Credit history (server paged) ---------------------------------------

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export type CreditHistoryUrlDefaults = {
  chFrom: string;
  chTo: string;
  chReason: string;
  chBucket: string;
  chPage: string;
};

/**
 * Credit history's URL params. `chFrom`/`chTo` default to the last 30 days ending today,
 * the window the ledger has always opened on, on the calendar of `zone` (the location's,
 * DNKADM-0439; `null` is the device). Both ends are inclusive days. Compute it once per zone
 * (`useMemo` on the zone): the URL hook's memo and `set` depend on the defaults object.
 */
export function creditHistoryUrlDefaults(
  zone: string | null = null,
  now: number = Date.now(),
): CreditHistoryUrlDefaults {
  const range = rangeBounds(zone, 30, now);
  return {
    chFrom: range.from,
    chTo: range.to,
    chReason: '',
    chBucket: '',
    chPage: '1',
  };
}

/**
 * A `chFrom`/`chTo` URL value: a real `YYYY-MM-DD` day, `''` (cleared, no bound), or else
 * (a hand-edited or stale link) the default, so a bad date never reaches the API as a 400.
 */
export function parseCreditHistoryDate(raw: string, fallback: string): string {
  if (raw === '') return '';
  const match = ISO_DATE.exec(raw);
  if (match === null) return fallback;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  const real =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return real ? raw : fallback;
}

// --- Payment methods (client list, card rows) ---------------------------------------

export type CardSortKey = 'added' | 'card' | 'expires';
export const CARD_SORT_KEYS: readonly CardSortKey[] = ['added', 'card', 'expires'];
/** The server's order, newest card first, stays the default. */
export const CARD_DEFAULT_SORT: SortState<CardSortKey> = { key: 'added', dir: 'desc' };
export const CARD_SORT_OPTIONS: ReadonlyArray<SortControlOption<CardSortKey>> = [
  { value: 'added', label: 'Added', firstDir: 'desc' },
  { value: 'card', label: 'Card' },
  { value: 'expires', label: 'Expires' },
];
export const PAYMENT_METHODS_URL_DEFAULTS = { pmSort: '', pmDir: '' };

/**
 * What a card row shows for a sort key: the added date, the card label (brand and masked
 * number), or the expiry month. A card missing the field sorts last.
 */
export function cardSortValue(card: MemberCard, key: CardSortKey): SortValue {
  switch (key) {
    case 'added':
      return toTime(card.createdAt);
    case 'card':
      return card.cardBrand === null && card.maskedPan === null ? null : cardLabel(card);
    case 'expires':
      return card.expYear === null || card.expMonth === null
        ? null
        : Math.trunc(card.expYear) * 100 + Math.trunc(card.expMonth);
  }
}

// --- Payments (server paged) --------------------------------------------------------

export const MEMBER_PAYMENTS_URL_DEFAULTS = { payPage: '1' };

// --- Guests (client list, server status filter) -------------------------------------

export const GUEST_STATUS_FILTERS: readonly MemberGuestStatusFilter[] = [
  'ACTIVE',
  'ARCHIVED',
  'BLOCKED',
  'all',
];

export type GuestSortKey = 'lastInvited' | 'name' | 'status' | 'invites' | 'firstInvited';
export const GUEST_SORT_KEYS: readonly GuestSortKey[] = [
  'lastInvited',
  'name',
  'status',
  'invites',
  'firstInvited',
];
/** The server's order (`lastInvitedAt DESC`) stays the default. */
export const GUEST_DEFAULT_SORT: SortState<GuestSortKey> = { key: 'lastInvited', dir: 'desc' };
export const GUEST_SORT_OPTIONS: ReadonlyArray<SortControlOption<GuestSortKey>> = [
  { value: 'lastInvited', label: 'Last invited', firstDir: 'desc' },
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'invites', label: 'Times invited', firstDir: 'desc' },
  { value: 'firstInvited', label: 'First invited', firstDir: 'desc' },
];
export const GUESTS_URL_DEFAULTS = { guestStatus: 'all', guestSort: '', guestDir: '' };

const GUEST_STATUS_LABEL: Record<MemberGuestStatus, string> = {
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
  BLOCKED: 'Blocked',
};

/**
 * What a guest row shows for a sort key. `name` is the row's display name (passed in, so
 * the sort and the row use the same fallback chain); `status` is the chip label.
 */
export function guestSortValue(guest: MemberGuest, key: GuestSortKey, name: string): SortValue {
  switch (key) {
    case 'lastInvited':
      return toTime(guest.lastInvitedAt);
    case 'firstInvited':
      return toTime(guest.firstInvitedAt);
    case 'name':
      return name;
    case 'status':
      return guest.status === null ? null : GUEST_STATUS_LABEL[guest.status];
    case 'invites':
      return guest.totalInviteCount;
  }
}
